import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/Button';
import { Card, CardHeader, CardBody } from '../components/Card';
import { Alert } from '../components/Alert';
import { AddressInput } from '../components/AddressInput';
import { memberAPI } from '../api/memberAPI';
import { regionAPI } from '../api/regionAPI';
import { Avatar } from '../components/Avatar';
import { ImageCropModal } from '../components/ImageCropModal';
import { validatePhone, validateEmail, validateDate } from '../utils/validation';
import { toInputDate, isMinor } from '../utils/format';

const emptyForm = { first_name: '', last_name: '', middle_initial: '', email: '', phone: '', region_id: '', address: '', latitude: null, longitude: null, birthdate: '', wedding_anniversary: '' };

export function MemberDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isAdmin, user } = useAuth();
    const isNew = !id || id === 'new';

    const isRegionalLeader = !isAdmin && !user?.roles?.includes('intake') && user?.roles?.includes('regional_leader');
    const canEdit = isAdmin || user?.roles?.includes('regional_leader') || user?.roles?.includes('intake');
    const canToggleStatus = isAdmin || user?.roles?.includes('pastor') || user?.roles?.includes('intake');

    const [form, setForm] = useState({ ...emptyForm });
    const [regions, setRegions] = useState([]);
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [memberData, setMemberData] = useState(null);

    const photoInputRef = useRef(null);
    const [cropFile, setCropFile] = useState(null);

    const handlePhotoSelect = (e) => {
        const file = e.target.files[0];
        if (file) setCropFile(file);
        e.target.value = '';
    };

    const handleCroppedUpload = async (croppedFile) => {
        setCropFile(null);
        try {
            const res = await memberAPI.uploadPhoto(id, croppedFile);
            const photoUrl = res.data?.data?.photo_url;
            if (photoUrl) {
                setMemberData((prev) => ({ ...prev, photo_url: photoUrl }));
                setSuccess('Photo uploaded');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to upload photo');
        }
    };

    // Migrate to leader state
    const [showMigrate, setShowMigrate] = useState(false);
    const [migrateRole, setMigrateRole] = useState('regional_leader');
    const [migrateRegion, setMigrateRegion] = useState('');
    const [migrateLoading, setMigrateLoading] = useState(false);
    const [inviteLink, setInviteLink] = useState(null);

    useEffect(() => {
        regionAPI.getRegions().then((res) => {
            setRegions((res.data.data || {}).items || []);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        if (isNew) return;
        setLoading(true);
        memberAPI.getMember(id).then((res) => {
            const m = res.data.data || res.data;
            setMemberData(m);
            setForm({
                first_name: m.first_name || '',
                last_name: m.last_name || '',
                middle_initial: m.middle_initial || '',
                email: m.email || '',
                phone: m.phone || '',
                region_id: m.region_id || '',
                address: m.address || '',
                latitude: m.latitude || null,
                longitude: m.longitude || null,
                birthdate: toInputDate(m.birthdate),
                wedding_anniversary: toInputDate(m.wedding_anniversary),
            });
        }).catch((err) => {
            setError(err.response?.data?.message || 'Failed to load member');
        }).finally(() => setLoading(false));
    }, [id, isNew]);

    const minor = isMinor(form.birthdate);

    const regionName = (rid) => {
        const r = regions.find((r) => r.id === rid);
        return r ? r.name : rid || '-';
    };

    const validateForm = () => {
        const errors = {};
        const phoneErr = validatePhone(form.phone);
        if (phoneErr) errors.phone = phoneErr;
        const emailErr = validateEmail(form.email);
        if (emailErr) errors.email = emailErr;
        const bdErr = validateDate(form.birthdate, 'Birthdate');
        if (bdErr) errors.birthdate = bdErr;
        const waErr = validateDate(form.wedding_anniversary, 'Wedding anniversary');
        if (waErr) errors.wedding_anniversary = waErr;
        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = validateForm();
        setFormErrors(errors);
        if (Object.keys(errors).length > 0) return;
        setSaving(true);
        try {
            const payload = { ...form };
            if (minor) {
                delete payload.email;
                delete payload.phone;
                delete payload.address;
                delete payload.latitude;
                delete payload.longitude;
            }
            if (isNew && isRegionalLeader) {
                payload.region_id = user.region_id || payload.region_id;
            }
            if (isNew) {
                await memberAPI.createMember(payload);
                setSuccess('Member created');
            } else {
                await memberAPI.updateMember(id, payload);
                setSuccess('Member updated');
            }
            setTimeout(() => navigate('/members'), 600);
        } catch (err) {
            setError(err.response?.data?.message || 'Operation failed');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Delete this member?')) return;
        try {
            await memberAPI.deleteMember(id);
            navigate('/members');
        } catch (err) {
            setError('Failed to delete member');
        }
    };

    const handleToggleStatus = async () => {
        if (!memberData) return;
        const newStatus = (memberData.status || 'active') === 'active' ? 'inactive' : 'active';
        try {
            await memberAPI.toggleStatus(id, { status: newStatus });
            setMemberData({ ...memberData, status: newStatus });
            setSuccess(`Member is now ${newStatus}`);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update status');
        }
    };

    const handleMigrate = async () => {
        setMigrateLoading(true);
        try {
            const payload = { roles: [migrateRole] };
            if (migrateRegion) payload.region_id = migrateRegion;
            const res = await memberAPI.migrateToLeader(id, payload);
            const data = res.data?.data;
            if (data?.invite_token) {
                setInviteLink(`${window.location.origin}/signup?token=${data.invite_token}`);
            }
            setSuccess('Member migrated to leader successfully.');
            setShowMigrate(false);
        } catch (err) {
            setError(err.response?.data?.message || 'Migration failed');
        } finally {
            setMigrateLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="p-4 lg:p-6 max-w-content mx-auto animate-fade-in">
                <p className="text-center text-slate-500 py-6">Loading...</p>
            </div>
        );
    }

    return (
        <div className="p-4 lg:p-6 max-w-content mx-auto animate-fade-in">
            <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                <div>
                    <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">{isNew ? 'Create Member' : `Edit Member`}</h1>
                    {!isNew && memberData && <p className="text-sm text-slate-500 mt-0.5">{memberData.name}</p>}
                </div>
                <div>
                    <Button variant="secondary" onClick={() => navigate('/members')}>Back</Button>
                </div>
            </header>

            <main>
                {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}
                {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

                {inviteLink && (
                    <div className="card-base p-4 mb-4">
                        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-2">Invite Link</h3>
                        <p className="text-sm text-slate-500 mb-2">
                            Share this link with the new leader to complete signup:
                        </p>
                        <div className="flex gap-2 items-center">
                            <input type="text" value={inviteLink} readOnly className="input-field flex-1" />
                            <Button size="sm" onClick={() => { navigator.clipboard.writeText(inviteLink); setSuccess('Invite link copied!'); }}>Copy</Button>
                        </div>
                    </div>
                )}

                {!isNew && memberData && (
                    <div className="flex items-center gap-4 mb-4">
                        <Avatar name={memberData.name} photoUrl={memberData.photo_url} size="xl" />
                        {canEdit && (
                            <>
                                <input ref={photoInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handlePhotoSelect} />
                                <Button size="sm" variant="secondary" onClick={() => photoInputRef.current?.click()}>Change Photo</Button>
                            </>
                        )}
                    </div>
                )}
                {cropFile && <ImageCropModal file={cropFile} onCrop={handleCroppedUpload} onCancel={() => setCropFile(null)} />}

                <Card>
                    <CardHeader>
                        <h2>{isNew ? 'New Member' : 'Member Details'}</h2>
                    </CardHeader>
                    <CardBody>
                        <form className="grid sm:grid-cols-2 gap-4" onSubmit={handleSubmit}>
                            <div className="sm:col-span-2 flex gap-3">
                                <div className="flex flex-col gap-1.5 flex-1">
                                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">First Name *</label>
                                    <input className="input-field" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} disabled={!canEdit} />
                                </div>
                                <div className="flex flex-col gap-1.5 w-16">
                                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">M.I.</label>
                                    <input className="input-field text-center" maxLength={1} value={form.middle_initial} onChange={(e) => setForm({ ...form, middle_initial: e.target.value })} disabled={!canEdit} />
                                </div>
                                <div className="flex flex-col gap-1.5 flex-1">
                                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Last Name *</label>
                                    <input className="input-field" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} disabled={!canEdit} />
                                </div>
                            </div>
                            {minor && (
                                <div className="sm:col-span-2 px-3 py-2 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 text-sm text-amber-800 dark:text-amber-300">
                                    Contact information is hidden for members under 18.
                                </div>
                            )}
                            {!minor && (
                                <>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Email</label>
                                        <input className="input-field" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!canEdit} />
                                        {formErrors.email && <span className="text-red-500 text-xs mt-0.5">{formErrors.email}</span>}
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Phone</label>
                                        <input className="input-field" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} disabled={!canEdit} />
                                        {formErrors.phone && <span className="text-red-500 text-xs mt-0.5">{formErrors.phone}</span>}
                                    </div>
                                    <div className="flex flex-col gap-1.5 sm:col-span-2">
                                        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Address</label>
                                        <AddressInput
                                            value={form.address}
                                            onChange={({ address, latitude, longitude }) => setForm({ ...form, address, latitude, longitude })}
                                            disabled={!canEdit}
                                        />
                                    </div>
                                </>
                            )}
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Birthdate</label>
                                <input className="input-field" type="date" max={new Date().toISOString().split('T')[0]} value={form.birthdate} onChange={(e) => setForm({ ...form, birthdate: e.target.value })} disabled={!canEdit} />
                                {formErrors.birthdate && <span className="text-red-500 text-xs mt-0.5">{formErrors.birthdate}</span>}
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Wedding Anniversary</label>
                                <input className="input-field" type="date" max={new Date().toISOString().split('T')[0]} value={form.wedding_anniversary} onChange={(e) => setForm({ ...form, wedding_anniversary: e.target.value })} disabled={!canEdit} />
                                {formErrors.wedding_anniversary && <span className="text-red-500 text-xs mt-0.5">{formErrors.wedding_anniversary}</span>}
                            </div>
                            {!isRegionalLeader && (
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Region *</label>
                                    <select className="input-field" required value={form.region_id} onChange={(e) => setForm({ ...form, region_id: e.target.value })} disabled={!canEdit}>
                                        <option value="">Select a region</option>
                                        {regions.map((r) => (
                                            <option key={r.id} value={r.id}>{r.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            {canEdit && (
                                <div className="flex gap-3 sm:col-span-2 mt-2">
                                    <Button type="submit" disabled={saving}>{saving ? 'Saving...' : (isNew ? 'Create' : 'Update')}</Button>
                                    <Button variant="secondary" type="button" onClick={() => navigate('/members')}>Cancel</Button>
                                </div>
                            )}
                        </form>
                    </CardBody>
                </Card>

                {!isNew && memberData && (canToggleStatus || isAdmin) && (
                    <div className="card-base border-red-200 dark:border-red-800 p-4 mt-6">
                        <h3 className="text-sm font-semibold text-red-600 dark:text-red-400 mb-3">Actions</h3>
                        <div className="flex flex-wrap gap-2">
                            {canToggleStatus && (
                                <Button variant="secondary" onClick={handleToggleStatus}>
                                    {(memberData.status || 'active') === 'active' ? 'Deactivate' : 'Activate'}
                                </Button>
                            )}
                            {isAdmin && (
                                <>
                                    <Button variant="secondary" onClick={() => { setShowMigrate(!showMigrate); setInviteLink(null); }}>
                                        Make Leader
                                    </Button>
                                    <Button variant="danger" onClick={handleDelete}>Delete</Button>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {showMigrate && (
                    <Card className="mt-4">
                        <CardHeader>
                            <h2>Migrate to Leader</h2>
                        </CardHeader>
                        <CardBody>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Role *</label>
                                    <select className="input-field" value={migrateRole} onChange={(e) => setMigrateRole(e.target.value)}>
                                        <option value="regional_leader">Regional Leader</option>
                                        <option value="regional_assistant">Regional Assistant</option>
                                        <option value="pastor">Pastor</option>
                                        <option value="intake">Intake</option>
                                    </select>
                                </div>
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Region</label>
                                    <select className="input-field" value={migrateRegion} onChange={(e) => setMigrateRegion(e.target.value)}>
                                        <option value="">
                                            {memberData?.region_id ? `Keep current (${regionName(memberData.region_id)})` : 'Select a region'}
                                        </option>
                                        {regions.map((r) => (
                                            <option key={r.id} value={r.id}>{r.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <p className="text-sm text-slate-500 sm:col-span-2">
                                    This will delete the member record and create a leader account with a pending invite.
                                </p>
                                <div className="flex gap-3 sm:col-span-2 mt-2">
                                    <Button onClick={handleMigrate} disabled={migrateLoading}>
                                        {migrateLoading ? 'Migrating...' : 'Confirm Migration'}
                                    </Button>
                                    <Button variant="secondary" onClick={() => setShowMigrate(false)}>Cancel</Button>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                )}
            </main>
        </div>
    );
}

export default MemberDetail;
